/**
 * CircuitThumbnail — a compact "component-kind fingerprint" strip
 * for a Circuit, sized for the collapsed CircuitCard.
 *
 * Why a histogram, not a layout preview:
 *   * At 24px tall, an ELK-laid schematic is unreadable. A kind
 *     histogram is instantly scannable — "resistor-heavy passive
 *     board" looks different from "IC-heavy digital board" at a
 *     glance.
 *   * No ELK call, no react-flow worker — 50× cheaper to render than
 *     CircuitCanvas. Crucial when 6-8 cards land on screen at once.
 *
 * Performance posture:
 *   * IntersectionObserver gates the IR fetch — cards below the fold
 *     pay nothing until scrolled into view.
 *   * `useVersionIr` is shared-cached by React Query, so two cards
 *     pointing at the same version (e.g. main + a fresh candidate)
 *     share one fetch.
 *
 * Empty states:
 *   * No `current_version_id` → render nothing (parent gates this).
 *   * IR loading → narrow phosphor pulse so the slot doesn't collapse
 *     and re-flow when data arrives.
 */
import type { ComponentKind } from "@models/circuit-ir";
import { Box, Tooltip } from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";

import { useVersionIr } from "../hooks/useCircuitIr";

interface CircuitThumbnailProps {
  versionId: string;
  /** Pixel height of the strip. Defaults to 24 — matches the
   *  card's other dense rows. */
  height?: number;
}

// Stable ordering: passives first, then active discretes, then ICs.
// Matches the way an engineer scans a BOM ("Rs, Cs, Ls, then chips").
const KIND_ORDER: ComponentKind[] = [
  "resistor",
  "capacitor",
  "inductor",
  "diode",
  "mosfet",
  "bjt",
  "opamp",
  "ic",
  "header",
  "source",
  "rail",
  "other",
];

const KIND_LABEL: Record<ComponentKind, string> = {
  resistor: "Resistors",
  capacitor: "Capacitors",
  inductor: "Inductors",
  diode: "Diodes",
  mosfet: "MOSFETs",
  bjt: "BJTs",
  opamp: "Op-amps",
  ic: "ICs",
  header: "Headers",
  source: "Sources",
  rail: "Rails",
  other: "Other",
};

export function CircuitThumbnail({
  versionId,
  height = 24,
}: CircuitThumbnailProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  // Observe once — flip to visible the first time the card scrolls
  // into view and never unmount (avoids a flicker if the user scrolls
  // past and back).
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin: "120px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const { data } = useVersionIr(visible ? versionId : null);

  return (
    <Box
      ref={ref}
      sx={(t) => ({
        height,
        borderRadius: 0.75,
        bgcolor: alpha(t.palette.surface.sunken, 0.6),
        border: `1px solid ${alpha(t.palette.border.default, 0.7)}`,
        overflow: "hidden",
        position: "relative",
      })}
    >
      {data ? (
        <KindHistogram components={data.ir.components} height={height} />
      ) : (
        <LoadingPulse height={height} />
      )}
    </Box>
  );
}

interface KindHistogramProps {
  components: ReadonlyArray<{ kind: ComponentKind }>;
  height: number;
}

function KindHistogram({ components, height }: KindHistogramProps) {
  const theme = useTheme();

  // Count by kind. Single pass; component lists are small (<1k).
  const counts: Partial<Record<ComponentKind, number>> = {};
  for (const c of components) {
    counts[c.kind] = (counts[c.kind] ?? 0) + 1;
  }
  const total = components.length;
  if (total === 0) {
    return (
      <Box
        sx={(t) => ({
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.disabled",
          fontFamily: t.fonts.mono,
          fontSize: 10,
          letterSpacing: "0.08em",
        })}
      >
        EMPTY
      </Box>
    );
  }

  const palette: Record<ComponentKind, string> = {
    resistor: alpha(theme.palette.text.disabled, 0.7),
    capacitor: theme.palette.info.main,
    inductor: theme.palette.secondary.main,
    diode: theme.palette.warning.main,
    mosfet: theme.palette.error.main,
    bjt: alpha(theme.palette.error.main, 0.7),
    opamp: theme.palette.success.main,
    ic: theme.palette.primary.main,
    header: alpha(theme.palette.text.secondary, 0.65),
    source: alpha(theme.palette.editorial.main, 0.7),
    rail: alpha(theme.palette.editorial.main, 0.45),
    other: alpha(theme.palette.text.disabled, 0.4),
  };

  // Build the breakdown rows for the tooltip in the same KIND_ORDER as
  // the bar so the eye can map segment → row visually.
  const breakdown = KIND_ORDER.filter((k) => (counts[k] ?? 0) > 0).map((k) => ({
    kind: k,
    count: counts[k] ?? 0,
  }));

  const tooltipTitle = (
    <Box sx={(t) => ({ fontFamily: t.fonts.mono, fontSize: 11 })}>
      {breakdown.map((row) => (
        <Box
          key={row.kind}
          sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}
        >
          <span>{KIND_LABEL[row.kind]}</span>
          <span>{row.count}</span>
        </Box>
      ))}
      <Box sx={{ mt: 0.5, opacity: 0.65 }}>{total} total</Box>
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box sx={{ display: "flex", height: "100%", cursor: "default" }}>
        {breakdown.map((row) => (
          <Box
            key={row.kind}
            sx={{
              flexGrow: row.count,
              flexBasis: 0,
              bgcolor: palette[row.kind],
              minWidth: 2,
              // Faint divider so adjacent segments of similar colour
              // remain distinguishable. Not a border (would inflate
              // width) — a right-edge box-shadow.
              boxShadow: `inset -1px 0 0 ${alpha("#000", 0.18)}`,
              "&:last-of-type": { boxShadow: "none" },
            }}
          />
        ))}
      </Box>
    </Tooltip>
  );
}

const pulse = keyframes`
  0%   { opacity: 0.25; }
  50%  { opacity: 0.55; }
  100% { opacity: 0.25; }
`;

function LoadingPulse({ height }: { height: number }) {
  return (
    <Box
      sx={(t) => ({
        height,
        width: "100%",
        background: `linear-gradient(90deg, transparent 0%, ${alpha(
          t.palette.editorial.main,
          0.35,
        )} 50%, transparent 100%)`,
        animation: `${pulse} 1600ms ease-in-out infinite`,
      })}
    />
  );
}
